// Import Libraries
import React, { useContext, useMemo, useState } from 'react';
import { Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

// Import Components
import UpgradeMembership from '../../Modals/Subscription/upgradeMembership.js';

// Context
import ScriptContext from '../../../context/Script';
import { UserContext } from '../../../context/User';
import ProjectViewContext from '../../../context/ProjectView';

// Import Services
import ScriptService from '../../../services/scriptService';

// Construct Component
// Left half of the panel-tools row: "Scenes" + count, Generate All Scenes
// (when there's something to batch) and Add scene.
const ScenesToolbar = () => {
  const { script } = useContext(ScriptContext);
  const { user } = useContext(UserContext);
  const { view } = useContext(ProjectViewContext);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  const scenes = script?.scenes || [];

  // Only ai_status 0 counts: that's all triggerAllSceneGeneration will pick up.
  // Manually added scenes are -1, so they'd keep the button up with nothing to batch.
  const ungeneratedScenes = useMemo(
    () =>
      scenes.filter(
        (scene) => scene.ai_status === 0 && (!scene.shot_list || scene.shot_list.length === 0),
      ).length,
    [scenes],
  );

  // Free-tier scripts on their 1st/2nd upload auto-generate all scenes on upload —
  // there's nothing left to manually "Generate All", so hide the button entirely.
  const isFreemiumAutoGenerated =
    (script?.features || '').toLowerCase() !== 'pro' && script?.freemium_slot != null;

  // Also hidden on the sample project, for manual scripts (no PDF), which only
  // generate one scene at a time, and whenever there isn't more than one scene
  // left to generate.
  const showGenerateAll =
    !script?.is_sample &&
    !isFreemiumAutoGenerated &&
    !view.manualEntry &&
    !!view.uploadedFile &&
    ungeneratedScenes > 1;

  const canGenerateAll =
    user?.level?.toLowerCase() === 'pro' &&
    script?.features?.toLowerCase() === 'pro' &&
    script?.level !== 'viewer';

  const addScene = async () => {
    try {
      await ScriptService.createManualScene(script.id, scenes.length + 1);
    } catch (error) {
      console.error('Error adding scene:', error);
    }
  };

  const generateAllScenes = () => {
    if (!canGenerateAll) {
      setUpgradeModalOpen(true);
      return;
    }
    ScriptService.getShotListsForScript(script.id);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" component="h2">
        Scenes
      </Typography>
      <Chip label={scenes.length} aria-label={`${scenes.length} scenes`} />

      <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ ml: 'auto' }}>
        {showGenerateAll && (
          <Tooltip title={canGenerateAll ? 'Generate shotlists for every scene' : 'Pro feature. Click to upgrade.'}>
            <Button
              variant="outlined"
              onClick={generateAllScenes}
              startIcon={canGenerateAll ? <AutoAwesomeOutlinedIcon /> : <WorkspacePremiumIcon />}
            >
              Generate all
            </Button>
          </Tooltip>
        )}
        <Button id="manualEntry" startIcon={<AddIcon />} onClick={addScene}>
          Add scene
        </Button>
      </Stack>

      <UpgradeMembership
        isOpen={upgradeModalOpen}
        onRequestClose={() => setUpgradeModalOpen(false)}
        featureMsg=""
        scriptId={script?.id}
      />
    </Stack>
  );
};

export default ScenesToolbar;
